import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { JournalPost, JournalPostSummary } from '@/types'

import Link from 'next/link'
import { useRef } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { getAdjacentPosts, getPostBySlug } from '@/lib/data/journal'
import { formatLongDate } from '@/lib/format'
import { useEntrance } from '@/components/motion/reveal'
import Marquee from '@/components/motion/marquee'
import Parallax from '@/components/motion/parallax'
import Img from '@/components/generals/img'
import Star from '@/components/generals/star'

interface ArticleProps {
    post: JournalPost
    previous: JournalPostSummary | null
    next: JournalPostSummary | null
}

const BAND = ['Essentials 04', 'Read slowly', 'Wear longer', 'Join the list']

const Article: FC<ArticleProps> = ({ post, previous, next }): ReactNode => {
    const header = useRef<HTMLElement>(null)

    useEntrance(header, timeline => {
        timeline
            .from('[data-ar-meta]', { autoAlpha: 0, y: 12, duration: 1 }, 0)
            .from('[data-ar-title]', { yPercent: 105, duration: 1.4, ease: 'expo.out' }, 0.05)
            .from('[data-ar-excerpt]', { autoAlpha: 0, y: 24, filter: 'blur(8px)', duration: 1.2, clearProps: 'filter' }, 0.4)
            .from('[data-ar-byline]', { autoAlpha: 0, y: 16, duration: 1 }, 0.55)
            .from('[data-ar-cover]', { clipPath: 'inset(0% 0% 100% 0%)', duration: 1.6, ease: 'expo.inOut' }, 0.3)
    })

    return (
        <Layout>
            <Seo
                title={post.title}
                description={post.excerpt || `${post.title}, a story from the Elio journal.`}
                ogImage={post.cover || undefined}
                type='article'
            />

            <article>
                <header ref={header} className='pt-[calc(var(--header-height)+56px)] md:pt-[calc(var(--header-height)+88px)]'>
                    <div className='mx-auto flex max-w-5xl flex-col items-center text-center gutter'>
                        <p data-ar-meta className='flex items-center gap-4 meta-mono'>
                            <span className='text-acid' data-mdlfy-cms={`journal_posts:${post.id}:category`}>{post.category}</span>

                            <span aria-hidden='true' className='h-px w-10 bg-line' />

                            {post.read_time ? <span className='text-ash' data-mdlfy-cms={`journal_posts:${post.id}:read_time`}>{post.read_time}</span> : null}
                        </p>

                        <div className='mt-6 overflow-hidden pb-[0.04em]'>
                            <h1 data-ar-title className='display-xl uppercase'>
                                <span data-mdlfy-cms={`journal_posts:${post.id}:title`}>{post.title}</span>
                            </h1>
                        </div>

                        {post.excerpt ? (
                            <p data-ar-excerpt className='mt-8 max-w-[34ch] serif-m text-bone/90'>
                                <span data-mdlfy-cms={`journal_posts:${post.id}:excerpt`}>{post.excerpt}</span>
                            </p>
                        ) : null}

                        <p data-ar-byline className='mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 meta-mono'>
                            {post.author ? <span className='text-bone' data-mdlfy-cms={`journal_posts:${post.id}:author`}>{post.author}</span> : null}

                            <time dateTime={post.published_on} className='text-ash'>
                                {formatLongDate(post.published_on)}
                            </time>
                        </p>
                    </div>

                    <div data-ar-cover className='mt-14 md:mt-20'>
                        <Parallax amount={10} className='h-[58vh] md:h-[82vh]'>
                            <Img src={post.cover} alt={post.cover_alt || post.title} sizes='100vw' preload className='object-[50%_25%]' />
                        </Parallax>
                    </div>
                </header>

                <div className='py-20 gutter md:py-28'>
                    {post.body ? (
                        <div
                            data-reveal='fade'
                            className='prose-elio mx-auto max-w-[680px]'
                            dangerouslySetInnerHTML={{ __html: post.body }}
                        />
                    ) : null}
                </div>
            </article>

            <nav aria-label='More stories' className='grid grid-cols-3 items-center gap-4 border-t border-line py-6 gutter'>
                {previous ? (
                    <Link href={`/journal/${previous.slug}`} data-transition-label={previous.title} className='group flex items-center gap-2 label-mono text-bone transition-colors duration-300 hover:text-acid'>
                        <ArrowLeft aria-hidden='true' className='size-4 transition-transform duration-500 group-hover:-translate-x-1' />
                        <span>Previous<span className='hidden sm:inline'> story</span></span>
                    </Link>
                ) : <span />}

                <Link href='/journal' className='justify-self-center label-mono text-bone transition-colors duration-300 hover:text-acid'>
                    All stories
                </Link>

                {next ? (
                    <Link href={`/journal/${next.slug}`} data-transition-label={next.title} className='group flex items-center justify-end gap-2 justify-self-end label-mono text-bone transition-colors duration-300 hover:text-acid'>
                        <span>Next<span className='hidden sm:inline'> story</span></span>
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-500 group-hover:translate-x-1' />
                    </Link>
                ) : <span />}
            </nav>

            <div aria-hidden='true' className='bg-acid py-5 text-ink'>
                <Marquee speed={70} velocity={0.18}>
                    {BAND.map(item => (
                        <span key={item} className='flex items-center gap-10 pr-10 font-heading text-[clamp(40px,5.4vw,84px)] leading-none'>
                            {item}

                            <Star />
                        </span>
                    ))}
                </Marquee>
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<ArticleProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    const post = await getPostBySlug(slug)

    if (!post) return { notFound: true }

    const { previous, next } = await getAdjacentPosts(slug).catch(() => ({ previous: null, next: null }))

    return { props: { post, previous, next } }
}

export default Article
